import { useEffect, useMemo, useState } from "react";
import { Check, Loader2, MessageSquare, Search, Users } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { fetchInternalChatPartners } from "@/lib/api/users";
import { getAvailabilityMap, getOnlineUserIds, subscribePresence } from "@/lib/realtime/presence-service";
import PresenceDot from "@/components/presence/presence-dot";
import { useIsAdmin } from "@/hooks/use-permissions";

function partnerLabel(partner) {
  // Prefer the person's actual first + last name; fall back to displayName / id only when the
  // profile carries no name, so a row is never blank.
  const composed = [partner?.firstName, partner?.lastName]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(" ");
  if (composed) return composed;
  const display = String(partner?.displayName || "").trim();
  if (display) return display;
  return String(partner?.id || "").trim() || "Unknown";
}

/**
 * Start a new internal staff chat. Direct mode picks exactly one partner (the backend reuses an
 * existing 1:1 thread if there is one); Group mode picks several partners plus a name. The
 * partner directory is server-filtered to the configured eligible user types AND the
 * per-access-group chat visibility facet (context=chat). Admins can opt into "Show hidden"
 * to list every active eligible partner plus doctors (includeHidden), even when doctor is
 * omitted from eligibleUserTypes.
 *
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {(args: { name?: string, userIds: string[], kind: "direct" | "group" }) => Promise<any>} props.onCreate
 * @param {Set<string>} [props.existingDirectPartnerIds] — people the caller already has a 1:1
 *   chat with; hidden in DIRECT mode (the chat is already in the list), still selectable in
 *   Group mode.
 */
export function NewChatDialog({ open, onOpenChange, onCreate, existingDirectPartnerIds = null }) {
  const isAdmin = useIsAdmin();
  const [mode, setMode] = useState("direct");
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [query, setQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  // Admin-only: include directory-hidden active staff in the picker.
  const [showHidden, setShowHidden] = useState(false);
  // Presence for the partner rows: one open-gated subscription for the whole list.
  const [presenceTick, setPresenceTick] = useState(0);
  useEffect(() => {
    if (!open) return undefined;
    setPresenceTick((n) => n + 1);
    return subscribePresence(() => setPresenceTick((n) => n + 1));
  }, [open]);
  const onlineIds = getOnlineUserIds();
  const availabilityMap = getAvailabilityMap();
  void presenceTick; // read tick so re-renders track presence changes

  // Clear admin "Show hidden" when the dialog closes so the next open starts filtered.
  useEffect(() => {
    if (!open) setShowHidden(false);
  }, [open]);

  // Reset transient picker state when the dialog opens (not when showHidden flips).
  useEffect(() => {
    if (!open) return;
    setMode("direct");
    setSelectedIds([]);
    setGroupName("");
    setQuery("");
    setSubmitError("");
  }, [open]);

  // Load the eligible partner directory whenever the dialog opens or an admin toggles
  // "Show hidden" (includeHidden lists every active eligible partner).
  useEffect(() => {
    if (!open) return;
    setLoadError("");
    setLoading(true);
    let cancelled = false;
    const includeHidden = isAdmin && showHidden;
    fetchInternalChatPartners({ context: "chat", includeHidden })
      .then((res) => {
        if (cancelled) return;
        setPartners(Array.isArray(res?.items) ? res.items : []);
      })
      .catch(() => {
        if (cancelled) return;
        setPartners([]);
        setLoadError("Failed to load people you can chat with.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, isAdmin, showHidden]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  // People with an existing 1:1 chat disappear from DIRECT mode only — that conversation
  // already sits in the list; clicking it there is the faster path. Group mode keeps
  // everyone (a group needs them regardless).
  const allDirectFiltered = useMemo(() => {
    if (mode !== "direct" || !(existingDirectPartnerIds instanceof Set) || existingDirectPartnerIds.size === 0) {
      return false;
    }
    return partners.length > 0 && partners.every((p) => existingDirectPartnerIds.has(String(p?.id || "").trim()));
  }, [mode, partners, existingDirectPartnerIds]);

  const filteredPartners = useMemo(() => {
    const q = query.trim().toLowerCase();
    let base = partners;
    if (mode === "direct" && existingDirectPartnerIds instanceof Set && existingDirectPartnerIds.size > 0) {
      base = base.filter((partner) => !existingDirectPartnerIds.has(String(partner?.id || "").trim()));
    }
    if (!q) return base;
    return base.filter((partner) => {
      const label = partnerLabel(partner).toLowerCase();
      const type = String(partner?.userType || "").toLowerCase();
      return label.includes(q) || type.includes(q);
    });
  }, [partners, query, mode, existingDirectPartnerIds]);

  const togglePartner = (partnerId) => {
    const id = String(partnerId || "").trim();
    if (!id) return;
    setSubmitError("");
    if (mode === "direct") {
      // Direct chat targets exactly one person; selecting another replaces the choice.
      setSelectedIds((prev) => (prev[0] === id ? [] : [id]));
      return;
    }
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const switchMode = (nextMode) => {
    if (nextMode === mode) return;
    setMode(nextMode);
    setSelectedIds([]);
    setSubmitError("");
  };

  const trimmedName = groupName.trim();
  const canSubmit =
    !submitting &&
    (mode === "direct" ? selectedIds.length === 1 : selectedIds.length >= 1 && trimmedName.length > 0);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      await onCreate?.({
        kind: mode,
        userIds: selectedIds,
        ...(mode === "group" ? { name: trimmedName } : {}),
      });
      onOpenChange?.(false);
    } catch (error) {
      setSubmitError(error?.message || "Could not start the chat. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New chat</DialogTitle>
          <DialogDescription>Start an internal chat with another staff member.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2">
          <Button
            type="button"
            variant={mode === "direct" ? "default" : "outline"}
            size="sm"
            onClick={() => switchMode("direct")}
          >
            <MessageSquare className="mr-1 h-4 w-4" />
            Direct
          </Button>
          <Button
            type="button"
            variant={mode === "group" ? "default" : "outline"}
            size="sm"
            onClick={() => switchMode("group")}
          >
            <Users className="mr-1 h-4 w-4" />
            Group
          </Button>
        </div>

        {mode === "group" ? (
          <Input
            value={groupName}
            onChange={(event) => setGroupName(event.target.value)}
            placeholder="Group name"
            maxLength={120}
          />
        ) : null}

        <div className="rounded-md border">
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search staff…"
              className="h-10 border-0 px-0 shadow-none focus-visible:ring-0"
            />
          </div>
          {isAdmin ? (
            <label
              className="flex cursor-pointer select-none items-center gap-1.5 border-b px-3 py-2 text-xs text-muted-foreground"
              title="Include directory-hidden staff and doctors (all active)"
            >
              <input
                type="checkbox"
                checked={showHidden}
                onChange={(e) => setShowHidden(e.target.checked)}
                className="size-3.5 accent-primary"
              />
              Show hidden (all active, including doctors)
            </label>
          ) : null}
          <ScrollArea className="h-64">
            <div className="p-1">
              {loading ? (
                <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading…
                </div>
              ) : loadError ? (
                <div className="py-6 text-center text-sm text-destructive">{loadError}</div>
              ) : filteredPartners.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  {allDirectFiltered
                    ? "You already have a chat with everyone here — switch to Group to include them."
                    : "No staff found."}
                </div>
              ) : (
                filteredPartners.map((partner) => {
                  const id = String(partner?.id || "").trim();
                  const label = partnerLabel(partner);
                  const userType = String(partner?.userType || "").trim();
                  const selected = selectedSet.has(id);
                  const online = onlineIds.has(id);
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => togglePartner(id)}
                      className={cn(
                        "flex w-full items-center gap-2 rounded-sm px-2 py-2 text-left text-sm transition-colors hover:bg-muted",
                        selected && "bg-muted",
                      )}
                    >
                      <span className="inline-flex w-3 shrink-0 justify-center">
                        {online ? (
                          <PresenceDot online availability={availabilityMap[id] || partner?.availability} />
                        ) : (
                          <span className="h-2.5 w-2.5 rounded-full border border-border/70" aria-label="Offline" title="Offline" />
                        )}
                      </span>
                      <span className="min-w-0 flex-1 truncate">{label}</span>
                      {userType ? (
                        <span className="shrink-0 text-xs capitalize text-muted-foreground">{userType}</span>
                      ) : null}
                      <Check className={cn("h-4 w-4 shrink-0", selected ? "opacity-100" : "opacity-0")} />
                    </button>
                  );
                })
              )}
            </div>
          </ScrollArea>
        </div>

        {mode === "group" && selectedIds.length > 0 ? (
          <p className="text-xs text-muted-foreground">
            {selectedIds.length} {selectedIds.length === 1 ? "person" : "people"} selected
          </p>
        ) : null}

        {submitError ? <p className="text-sm text-destructive">{submitError}</p> : null}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange?.(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            Start chat
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default NewChatDialog;
